import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";

const partyConfig = {
  D66_GE_p: { label: "D66", color: "#2cc800" },
  D66_LE_p: { label: "D66", color: "#2cc800" },
  PVV_GE_p: { label: "PVV", color: "#043555" },
  PVV_LE_p: { label: "PVV", color: "#043555" },
  VVD_GE_p: { label: "VVD", color: "#0e2783" },
  VVD_LE_p: { label: "VVD", color: "#0e2783" },
  GLpvdA_GE_p: { label: "GL–PvdA", color: "#920c19" },
  GLpvdA_LE_p: { label: "GL–PvdA", color: "#920c19" },
  CDA_GE_p: { label: "CDA", color: "#339933" },
  CDA_LE_p: { label: "CDA", color: "#339933" },
  OtherRight_GE_p: { label: "Other Right", color: "#7393B3" },
  OtherRight_LE_p: { label: "Other Right", color: "#7393B3" },
  OtherLeft_GE_p: { label: "Other Left", color: "#997570" },
  OtherLeft_LE_p: { label: "Other Left", color: "#997570" },
  OtherCentre_GE_p: { label: "Other Centre", color: "#D3D3D3" },
  OtherCentre_LE_p: { label: "Other Centre", color: "#D3D3D3" },
  Other_GE_p: { label: "Other", color: "#d4d2d2ff" },
 // Other_LE_p: { label: "Other", color: "#d4d2d2ff" },

  // Past vote
  past_vote_CDA: { label: "CDA", color: "#339933" },
  past_vote_D66: { label: "D66", color: "#2cc800" },
  past_vote_DidNotVote: { label: "Did not vote", color: "#94A3B8" }, // not in PARTY_INFO, kept as-is
  past_vote_GLpvdA: { label: "GL–PvdA", color: "#920c19" },
  past_vote_OtherCentre: { label: "Other centre", color: "#999999" },
  past_vote_OtherLeft: { label: "Other left", color: "#997570" },
  past_vote_OtherRight: { label: "Other right", color: "#7393B3" },
  past_vote_PVV: { label: "PVV", color: "#043555" },
  past_vote_VVD: { label: "VVD", color: "#0e2783" },
};

// D’Hondt / largest averages allocator.
// Uses any proportional values (counts, %, shares) — only ratios matter.
function allocateSeatsDHondt(votesByParty, totalSeats) {
  const parties = Object.keys(votesByParty);
  const seats = Object.fromEntries(parties.map((p) => [p, 0]));

  for (let i = 0; i < totalSeats; i += 1) {
    let bestParty = null;
    let bestQuotient = -Infinity;

    for (const p of parties) {
      const v = votesByParty[p] ?? 0;
      const q = v / (seats[p] + 1);
      if (q > bestQuotient) {
        bestQuotient = q;
        bestParty = p;
      }
    }

    if (bestParty) seats[bestParty] += 1;
  }

  return seats;
}

// matcher because named the columns stupidly
function getGroupKeys(polLevel) {
  const forward =
    polLevel === "past"
      ? {
          D66: "past_vote_D66",
          CDA: "past_vote_CDA",
          'GL-PvdA': "past_vote_GLpvdA",
          OtherCentre: "past_vote_OtherCentre",
          OtherLeft: "past_vote_OtherLeft",
          OtherRight: "past_vote_OtherRight",
          PVV: "past_vote_PVV",
          VVD: "past_vote_VVD",
        }
      : (() => {
          const level = polLevel === "LE" ? "LE" : "GE";

          return {
            D66: `D66_${level}_p`,
            PVV: `PVV_${level}_p`,
            VVD: `VVD_${level}_p`,
            'GL-PvdA': `GLpvdA_${level}_p`,
            CDA: `CDA_${level}_p`,
            OtherRight: `OtherRight_${level}_p`,
            OtherLeft: `OtherLeft_${level}_p`,
            OtherCentre: `OtherCentre_${level}_p`,
            // Other: `Other_${level}_p`,
            ...(polLevel === "LE" ? { Local: "Local_LE_p" } : {}), // ✅ only when LE
          //  ...(polLevel === "LE" ? { Other: `Other_${level}_p` } : {}), // ✅ only when LE
          };
        })();

  // invert it: longKey -> shortName
  return Object.fromEntries(
    Object.entries(forward).map(([shortName, longKey]) => [longKey, shortName])
  );
}

export default function ConstituencyGrid({
  polLevel = "GE", // "GE" | "LE" | "past"
  metricRowsNational = [],
  metricRowsConst = [],
  councilSeatsByArea = {},
  councilSeatsDefault = null,
  seatSquaresUnit = 1,
  square_size,
  selectedFeature,

  // ✅ NEW: hard-code seats when polLevel === "past"
  // keys = party SHORT names (e.g. { PVV: 37, VVD: 24, ... })
  pastHardcodedSeatsByParty = null,
}) {
  const safeArray = (v) => (Array.isArray(v) ? v : []);
  const natRows = safeArray(metricRowsNational);
  const constRows = safeArray(metricRowsConst);

  const rowsToUse = (() => {
    const selectedId = selectedFeature?.area_id;

    if (selectedId && constRows.length > 0) {
      const matched = constRows.find((g) => g?.area_id === selectedId);
      return matched ? [matched] : [];
    }

    // if nothing selected, use all constituencies/municipalities
    return constRows;
  })();

  // ✅ hooks must be unconditional, so we always compute these
  const longToShort = useMemo(() => {
    // if getGroupKeys is missing, fail gracefully rather than crashing
    if (typeof getGroupKeys !== "function") return {};
    return getGroupKeys(polLevel) || {};
  }, [polLevel]);

  const shortToLong = useMemo(() => {
    return Object.fromEntries(
      Object.entries(longToShort).map(([longKey, shortName]) => [shortName, longKey])
    );
  }, [longToShort]);

  const PARTY_SHORTS = useMemo(() => Object.keys(shortToLong), [shortToLong]);

  // Normalise values:
  // - 0..1 stays as share
  // - 0..100 becomes share (÷100)
  // - >100 treated as "votes-like" (DHondt still works)
  const toShareOrVotes = (v) => {
    const num = Number(v);
    //if (!Number.isFinite(num)) return 0;
    //if (num <= 1) return num;
    //if (num <= 100) return num / 100;
    return num;
  };

  const getPartyMeta = (shortName) => {
    const longKey = shortToLong[shortName];
    const meta =
      (longKey && partyConfig?.[longKey]) ||
      partyConfig?.[shortName] ||
      null;

    return {
      label: meta?.label ?? shortName,
      color: meta?.color ?? "grey",
      longKey,
      shortName,
    };
  };

  const geLikeData = useMemo(() => {
    // Covers GE + past (single national row -> 150 seats)
    if (natRows.length === 0 || PARTY_SHORTS.length === 0) {
      return { seatsPerParty: [] };
    }

    // ✅ NEW: if this is "past" and we have hard-coded seats, use them
    if (
      polLevel === "past" &&
      pastHardcodedSeatsByParty &&
      typeof pastHardcodedSeatsByParty === "object"
    ) {
      const seatsPerParty = PARTY_SHORTS
        .map((short) => ({
          partyShort: short,
          partyLong: shortToLong[short],
          partySeats: Number(pastHardcodedSeatsByParty?.[short] ?? 0),
        }))
        .sort((a, b) => b.partySeats - a.partySeats);

      return { seatsPerParty };
    }

    const row = natRows[0] ?? {};

    const shares = PARTY_SHORTS.map((short) => toShareOrVotes(row?.[shortToLong[short]]));
    const totalShare = shares.reduce((a, b) => a + b, 0);

    const seatsPerParty = PARTY_SHORTS
      .map((short, idx) => {
        const share = shares[idx] ?? 0;
        const partySeats = totalShare > 0 ? Math.round((share / totalShare) * 150) : 0;
        return { partyShort: short, partyLong: shortToLong[short], partySeats };
      })
      .sort((a, b) => b.partySeats - a.partySeats);

    return { seatsPerParty };
  }, [natRows, PARTY_SHORTS, shortToLong, polLevel, pastHardcodedSeatsByParty]);

  const leData = useMemo(() => {
    if (polLevel !== "LE") {
      return { seatsPerParty: [], municipalitiesUsed: 0, totalAllocatedSeats: 0 };
    }
    if (rowsToUse.length === 0 || PARTY_SHORTS.length === 0) {
      return { seatsPerParty: [], municipalitiesUsed: 0, totalAllocatedSeats: 0 };
    }

    const partyLongKeys = PARTY_SHORTS.map((short) => shortToLong[short]).filter(Boolean);

    const totals = Object.fromEntries(partyLongKeys.map((k) => [k, 0]));
    let municipalitiesUsed = 0;
    let totalAllocatedSeats = 0;

    for (const row of rowsToUse) {
      const areaId = row?.area_id;
      if (!areaId) continue;

      const seatsInCouncil = councilSeatsByArea?.[areaId] ?? councilSeatsDefault;
      if (!Number.isFinite(seatsInCouncil) || seatsInCouncil <= 0) continue;

      const votesByParty = {};
      let voteSum = 0;

      for (const k of partyLongKeys) {
        const v = toShareOrVotes(row?.[k]);
        votesByParty[k] = v;
        voteSum += v;
      }

      if (!(voteSum > 0)) continue;

      const allocatedHere = allocateSeatsDHondt(votesByParty, seatsInCouncil);

      for (const k of partyLongKeys) {
        totals[k] += allocatedHere?.[k] ?? 0;
      }

      municipalitiesUsed += 1;
      totalAllocatedSeats += seatsInCouncil;
    }

    const seatsPerParty = PARTY_SHORTS
      .map((short) => {
        const longKey = shortToLong[short];
        return {
          partyShort: short,
          partyLong: longKey,
          partySeats: longKey ? totals[longKey] ?? 0 : 0,
        };
      })
      .sort((a, b) => b.partySeats - a.partySeats);

    return { seatsPerParty, municipalitiesUsed, totalAllocatedSeats };
  }, [
    polLevel,
    rowsToUse,
    PARTY_SHORTS,
    shortToLong,
    councilSeatsByArea,
    councilSeatsDefault,
  ]);

  // ✅ only now do we conditionally return (after hooks)
  const haveAnyData = natRows.length > 0 || rowsToUse.length > 0;
  if (!haveAnyData) return null;

  const active =
    polLevel === "LE"
      ? { ...leData, mode: "LE" }
      : { ...geLikeData, mode: "GE_LIKE" };

  if (!active.seatsPerParty || active.seatsPerParty.length === 0) return null;

  return (
    <Box sx={{ px: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      {polLevel === "LE" && (
        <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
          Municipalities included:{" "}
          {active.municipalitiesUsed === 1 ? selectedFeature?.area_name : active.municipalitiesUsed} •{" "}
          Total seats allocated: {active.totalAllocatedSeats}
        </Typography>
      )}

      {polLevel === "GE" && (
        <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
          Tweede Kamer Election seat forecast:
          {active.totalAllocatedSeats}
        </Typography>
      )}

      {active.seatsPerParty.map((group) => {
        const meta = getPartyMeta(group.partyShort);

        return (
          <Box
            key={group.partyLong || group.partyShort}
            sx={{
              display: "grid",
              gridTemplateColumns: "100px 1fr 30px",
              alignItems: "center",
              columnGap: 1,
            }}
          >
            <Typography sx={{ fontSize: 14 }}>{meta.label}</Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {Array.from({
                length: Math.floor((group.partySeats ?? 0) / seatSquaresUnit),
              }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: square_size,
                    height: square_size,
                    bgcolor: meta.color,
                    borderRadius: "2px",
                  }}
                />
              ))}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {group.partySeats ?? 0}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
